import {Modal, Spin, Tag} from "antd";
import Axios from "axios";
import React, {useEffect, useState} from "react";
import {AiExtractorType, formatAiExtractorType} from "../../domain/AiExtractor";
import {formatDurationMs} from "../../utils/FormatUtils";
import * as styles from "./AiProviderCommunicationLogModal.module.css";

type AiProviderCommunicationLogDirection = "REQUEST" | "RESPONSE";

interface AiProviderCommunicationLogEntry {
    id: string;
    createdAt: string;
    runId: string;
    sequence: number;
    direction: AiProviderCommunicationLogDirection;
    operation: string;
    dryRun: boolean;
    providerType: string;
    model?: string;
    endpoint?: string;
    httpStatus?: number;
    executionTimeMs?: number;
    payload?: unknown;
    errorMessage?: string;
}

interface AiProviderCommunicationLogResponse {
    logs: AiProviderCommunicationLogEntry[];
}

interface Props {
    open: boolean;
    runId?: string;
    title?: string;
    onClose: () => void;
}

function AiProviderCommunicationLogModal(props: Props) {
    const [logs, setLogs] = useState<AiProviderCommunicationLogEntry[]>([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!props.open || !props.runId) {
            setLogs([]);
            return;
        }

        setLoading(true);
        Axios.get<AiProviderCommunicationLogResponse>(`/ai-provider-communication-logs/${encodeURIComponent(props.runId)}`)
            .then(response => setLogs(response.data.logs ?? []))
            .finally(() => setLoading(false));
    }, [props.open, props.runId]);

    return (
        <Modal
            title={props.title ?? "Logged AI provider calls"}
            open={props.open}
            onCancel={props.onClose}
            footer={null}
            mask={{closable: false}}
            destroyOnHidden={true}
            width={"90%"}
            style={{maxWidth: 1200}}
        >
            <Spin spinning={loading}>
                {logs.length === 0 && !loading && (
                    <div className={styles["empty"]}>No AI provider calls were logged.</div>
                )}

                {logs.map((log, index) => (
                    <div key={log.id} className={styles["log-block"]}>
                        <div className={styles["log-header"]}>
                            <div className={styles["log-title"]}>
                                <Tag color={providerCommunicationLogColor(log)}>
                                    {log.direction.toLowerCase()}
                                </Tag>
                                <h3 className={styles["log-label"]}>{formatLogProvider(log)}</h3>
                            </div>
                            <div className={styles["log-meta"]}>
                                <span>message {index + 1} / {logs.length}</span>
                                <span>{formatLogDateTime(new Date(log.createdAt))}</span>
                                {log.httpStatus != null && <span>HTTP {log.httpStatus}</span>}
                                {log.executionTimeMs != null && <span>{formatDurationMs(log.executionTimeMs)}</span>}
                            </div>
                        </div>
                        {log.errorMessage && <div className={styles["error"]}>{log.errorMessage}</div>}
                        <pre className={styles["payload"]}>{formatPayload(log.payload)}</pre>
                    </div>
                ))}
            </Spin>
        </Modal>
    );
}

function formatPayload(payload: unknown): string {
    if (payload === undefined || payload === null) {
        return "";
    }

    if (typeof payload === "string") {
        return payload;
    }

    return JSON.stringify(payload, null, 2);
}

function formatLogDateTime(date: Date) {
    return `${date.getFullYear()}-${padDatePart(date.getMonth() + 1)}-${padDatePart(date.getDate())} `
           + `${padDatePart(date.getHours())}:${padDatePart(date.getMinutes())}:${padDatePart(date.getSeconds())}.`
           + `${date.getMilliseconds().toString().padStart(3, "0")}`;
}

function formatLogProvider(log: AiProviderCommunicationLogEntry) {
    return [formatAiExtractorType(log.providerType as AiExtractorType), log.model]
            .filter(Boolean)
            .join(" · ");
}

function padDatePart(value: number) {
    return value.toString().padStart(2, "0");
}

function providerCommunicationLogColor(log: AiProviderCommunicationLogEntry) {
    if (log.direction === "REQUEST") {
        return "blue";
    }

    return log.errorMessage ? "red" : "green";
}

export default AiProviderCommunicationLogModal;
